"use client";

import type { Dispatch, SetStateAction } from "react";
import type { useRouter } from "next/navigation";
import type { KundaliResponse } from "@/lib/api/kundali";
import { rememberLastReading } from "@/lib/last-reading";
import {
  trackKundaliFormSubmitFailed,
  trackKundaliGenerated,
  trackKundaliResultViewed,
} from "@/lib/analytics-kundali";
import type { KundaliLang } from "./KundaliFormCard.i18n";
import type { KundaliFieldErrors } from "./KundaliFormCard.validation";

export function currentPathname(): string | undefined {
  return typeof window !== "undefined" ? window.location.pathname || undefined : undefined;
}

function focusInvalidField(field: keyof KundaliFieldErrors) {
  window.setTimeout(() => {
    document.getElementById(field)?.focus();
    document.getElementById(field)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, 0);
}

export function handleInvalidKundaliForm({
  validation,
  setFieldErrors,
  setError,
  language,
  birthTime,
  analyticsSource,
  sourcePath,
}: {
  validation: { errors: KundaliFieldErrors; firstField: keyof KundaliFieldErrors; ok: false };
  setFieldErrors: Dispatch<SetStateAction<KundaliFieldErrors>>;
  setError: Dispatch<SetStateAction<string | null>>;
  language: KundaliLang;
  birthTime: string;
  analyticsSource: string;
  sourcePath: string | undefined;
}) {
  setFieldErrors(validation.errors);
  setError(validation.errors[validation.firstField] || "Please check the form.");
  focusInvalidField(validation.firstField);
  trackKundaliFormSubmitFailed({
    language,
    has_time: !!birthTime,
    source: analyticsSource,
    source_path: sourcePath,
    error_message: validation.firstField,
  });
}

// Chat-first entries hand off to the dedicated /chat/<id> surface; result-first
// entries keep the inline result + chart. Add a page's KundaliFormCard `source`
// here to route it into the dedicated chat.
const CHAT_FIRST_SOURCES = new Set(["ai_astrologer", "free_astrology_chat", "nadi_astrology"]);

export function isChatFirstSource(source: string): boolean {
  return CHAT_FIRST_SOURCES.has(source);
}

/**
 * Post-generation handoff: persist + track, then either redirect a chat-first
 * entry to the dedicated /chat/<id> surface (returns true = navigated, so the
 * caller keeps the loading reveal through the transition) or swap the inline
 * result for result-first entries.
 */
export function finishKundali(
  response: KundaliResponse,
  ctx: {
    source: string;
    language: KundaliLang;
    birthTime: string;
    analyticsSource: string;
    sourcePath: string | undefined;
  },
  router: ReturnType<typeof useRouter>,
  setResult: (response: KundaliResponse) => void
): boolean {
  rememberLastReading({
    id: response.id,
    language: ctx.language,
    name: (response.birth_details as { name?: string } | undefined)?.name,
  });
  trackSuccessfulKundali(
    response,
    ctx.language,
    ctx.birthTime,
    ctx.analyticsSource,
    ctx.sourcePath
  );
  if (isChatFirstSource(ctx.source)) {
    router.push(`/chat/${response.id}?lang=${ctx.language}`);
    return true;
  }
  setResult(response);
  return false;
}

function trackSuccessfulKundali(
  response: KundaliResponse,
  language: KundaliLang,
  birthTime: string,
  source: string,
  sourcePath: string | undefined
) {
  trackKundaliGenerated({
    language,
    has_time: !!birthTime,
    source,
    source_path: sourcePath,
  });
  trackKundaliResultViewed({
    reading_id: response.id,
    language,
    has_time: !!birthTime,
    source,
    source_path: sourcePath,
    dosha_count: response.doshas.length,
    product_count: response.product_recommendations.length,
  });
}
